import React, { useState, useEffect } from 'react';
import { Key } from 'lucide-react';
import { describeAIError } from '../ai';
import { testClaudeKey } from '../ai/claudeProvider';
import { testGeminiKey, geminiImage } from '../ai/geminiProvider';

type TestName = 'claude' | 'gemini' | 'image';
type TestState = { status: 'idle' | 'running' | 'ok' | 'error'; message?: string };

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '0.25rem'
};

const APIKeysManager: React.FC = () => {
  const [claudeToken, setClaudeToken] = useState('');
  const [geminiToken, setGeminiToken] = useState('');
  const [saved, setSaved] = useState(false);
  const [tests, setTests] = useState<Record<TestName, TestState>>({
    claude: { status: 'idle' },
    gemini: { status: 'idle' },
    image: { status: 'idle' },
  });
  const [testImageUrl, setTestImageUrl] = useState<string | null>(null);

  useEffect(() => {
    setClaudeToken(localStorage.getItem('claudeToken') || '');
    setGeminiToken(localStorage.getItem('geminiToken') || '');
  }, []);

  useEffect(() => () => {
    if (testImageUrl) URL.revokeObjectURL(testImageUrl);
  }, [testImageUrl]);

  const handleSave = () => {
    localStorage.setItem('claudeToken', claudeToken.trim());
    localStorage.setItem('geminiToken', geminiToken.trim());
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const runTest = async (name: TestName, action: () => Promise<string>) => {
    setTests(prev => ({ ...prev, [name]: { status: 'running' } }));
    try {
      const message = await action();
      setTests(prev => ({ ...prev, [name]: { status: 'ok', message } }));
    } catch (err) {
      setTests(prev => ({ ...prev, [name]: { status: 'error', message: describeAIError(err) } }));
    }
  };

  const testClaude = () => runTest('claude', async () => {
    await testClaudeKey(claudeToken.trim());
    return 'Claude key works.';
  });

  const testGemini = () => runTest('gemini', async () => {
    await testGeminiKey(geminiToken.trim());
    return 'Gemini key works.';
  });

  const testImage = () => runTest('image', async () => {
    const blob = await geminiImage('A simple colorful drawing of a red kite in a blue sky, no text', geminiToken.trim());
    setTestImageUrl(URL.createObjectURL(blob));
    return 'Image generation works.';
  });

  const renderTestResult = (name: TestName) => {
    const state = tests[name];
    if (state.status === 'idle') return null;
    if (state.status === 'running') return <span style={{ color: '#6b7280' }}>Testing...</span>;
    return (
      <span style={{ color: state.status === 'ok' ? '#10b981' : '#ef4444', fontWeight: 500 }}>
        {state.message}
      </span>
    );
  };

  return (
    <div className="form-container">
      <h3>API Keys Configuration</h3>
      <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
        Configure the API keys used for AI suggestions and image generation. These keys are stored locally in your browser.
      </p>

      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Claude API Key</label>
        <input
          type="password"
          value={claudeToken}
          onChange={(e) => setClaudeToken(e.target.value)}
          placeholder="Enter Claude API Key..."
          style={inputStyle}
        />
        <small style={{ color: '#6b7280', display: 'block' }}>
          Used for all AI text (ideas, image prompts, fun facts) and for looking up songs on the web. Billed per call by Anthropic. If left blank, Gemini is used for text instead.
        </small>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
          <button
            type="button"
            onClick={testClaude}
            className="btn btn-secondary btn-small"
            disabled={!claudeToken.trim() || tests.claude.status === 'running'}
          >
            Test
          </button>
          {renderTestResult('claude')}
        </div>
      </div>

      <div className="form-group" style={{ marginBottom: '1.5rem' }}>
        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Google Gemini API Key</label>
        <input
          type="password"
          value={geminiToken}
          onChange={(e) => setGeminiToken(e.target.value)}
          placeholder="Enter Gemini API Key..."
          style={inputStyle}
        />
        <small style={{ color: '#6b7280', display: 'block' }}>
          Used for image generation, and for AI text when no Claude key is saved. If both are blank, text falls back to a free public AI (Pollinations).
        </small>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={testGemini}
            className="btn btn-secondary btn-small"
            disabled={!geminiToken.trim() || tests.gemini.status === 'running'}
          >
            Test
          </button>
          {renderTestResult('gemini')}
          <button
            type="button"
            onClick={testImage}
            className="btn btn-secondary btn-small"
            disabled={!geminiToken.trim() || tests.image.status === 'running'}
          >
            Test image generation (costs about one image)
          </button>
          {renderTestResult('image')}
        </div>
        {testImageUrl && (
          <img
            src={testImageUrl}
            alt="Test result"
            style={{ display: 'block', marginTop: '0.75rem', width: '160px', height: '160px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }}
          />
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '2rem' }}>
        <button onClick={handleSave} className="btn btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Key size={16} />
          Save API Keys
        </button>
        {saved && <span style={{ color: '#10b981', fontWeight: '500' }}>Keys saved successfully!</span>}
      </div>
    </div>
  );
};

export default APIKeysManager;
